import { useAppSelector } from '@/store/hooks';
import { selectIsSuperAdmin } from '@/store/selectors/me.selectors';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { selectReportingV2Widget } from '@/store/slices/reportingV2/reportingV2Slice';
import { useRef } from 'react';

export interface ReportingPermissions {
  isRibeyeAdmin: boolean;
  /** #4442 "Show DSP / Platform". */
  canViewPlatform: boolean;
  /** #4442 "Show wholesale Spend and CPM". */
  canViewWholesaleSpend: boolean;
  /** #4446 Client Spend/CPM. */
  canViewClientSpend: boolean;
  /** Attribution (conversions), flank#4648 — fed by the same always-fetched
   * `totals` widget as Spend/CPM below, since `totals` already requests
   * `conversions` (review, Lucas, flank#4705/ribeye-api#2495). */
  canViewAttribution: boolean;
  /** Raw inventory (apps/domains/publisher identity), flank#5097 — mirrors
   * ribeye-api's `resolveRawInventoryPermission` (rawInventory.ts), default
   * ON for Ribeye Admin only. The server already strips `publisher` (and the
   * `publishers` filter) and reports `dimensions.publisher` in
   * `restrictedFields` when denied, same convention as `dimensions.dsp`. */
  canViewRawInventory: boolean;
  /**
   * flank#4745 — distinct from the permission above: whether the CURRENT
   * filter selection actually has any resolved client_spend/client_cpm at
   * all, not whether the viewer is allowed to see it. Bonneville, e.g., has
   * the Client Spend permission granted but no external CPMs loaded yet (CS /
   * Partner Solutions hasn't filled them in Directus), so every row comes
   * back from `enrichWithClientSpend` with the metric entirely absent. A
   * metric picker gated only on `canViewClientSpend` then offers "Client
   * Spend" and draws an empty $0–$1 chart. Optimistically true until the
   * always-fetched `totals` widget resolves, same convention as the other
   * signals here.
   */
  hasClientSpendData: boolean;
}

/**
 * Pure decision logic, split out from the hook below so it's testable without
 * a Redux store (matches this directory's convention of unit-testing plain
 * exported functions — see clientMoneyColumns.test.ts).
 *
 * All three now read the same real, settable-today server-side signal —
 * ribeye-api#2401 shipped (it is no longer "on hold"), so an admin-granted
 * per-user override must be honored here the same way Client Spend already
 * works, not masked by a role-only fallback (review, flank#4650: "users an
 * admin granted still get spend/platform hidden").
 *
 * - "Show DSP / Platform" (#4442): the server strips the `dsp` dimension and
 *   reports `dimensions.dsp` in `restrictedFields` when denied. Fed by the
 *   `platformSignal` widget (see widgets.ts) — `totals` can't carry this
 *   signal itself, see that widget's own comment.
 * - "Show wholesale Spend and CPM" (#4442): the server strips `spend`/`cpm`
 *   and reports `metrics.spend` (and `metrics.cpm`, when requested) in
 *   `restrictedFields`. The `totals` widget requests `spend` but not `cpm`,
 *   so `metrics.spend` alone is the reliable signal — `metrics.cpm` would
 *   never appear even when denied, since nothing was there to strip.
 * - Client Spend/CPM (#4446): same convention, `metrics.client_spend`; the
 *   `totals` widget always requests both and is always fetched on page load
 *   (`useReportingV2`'s `loadAll`), so its restrictedFields is a reliable
 *   page-wide signal even for a widget (Top Campaigns, Top Publishers) that
 *   hasn't itself requested client_spend yet.
 */
export const derivePermissions = (
  isRibeyeAdmin: boolean,
  restrictedFields: string[],
  /** flank#4745 — defaults to the existing optimistic convention (true) so
   * every call site that predates this flag keeps its prior behavior. */
  hasClientSpendData = true,
): ReportingPermissions => ({
  isRibeyeAdmin,
  canViewPlatform: !restrictedFields.includes('dimensions.dsp'),
  canViewWholesaleSpend: !restrictedFields.includes('metrics.spend'),
  canViewClientSpend: !restrictedFields.includes('metrics.client_spend'),
  canViewAttribution: !restrictedFields.includes('metrics.conversions'),
  canViewRawInventory: !restrictedFields.includes('dimensions.publisher'),
  hasClientSpendData,
});

/** The slice of a widget's state this hook actually needs — kept separate from
 * the Redux `WidgetState` shape so this function stays testable without a store. */
export interface PermissionSignal {
  reqId?: string;
  loading: boolean;
  error: string | null;
  restrictedFields: string[];
}

/**
 * One signal's effective restrictedFields, pure and testable without Redux.
 *
 * `restrictedFields: []` is ambiguous on its own — it means both "confirmed
 * unrestricted" and "this signal's request hasn't succeeded yet" (the reducer
 * also resets it to [] on error). `pendingFields` is added synthetically until
 * THIS signal's own request actually succeeds, so a widget can't briefly show
 * Platform/Spend while the permission check is still in flight or after it
 * failed (review, flank#4650).
 */
export const effectiveRestrictedFields = (signal: PermissionSignal, pendingFields: string[]): string[] => [
  ...signal.restrictedFields,
  ...(!signal.reqId || signal.loading || signal.error ? pendingFields : []),
];

/**
 * Latches to `true` the first time a signal resolves (a real reqId, not
 * loading, no error) and never resets — a `WidgetState`'s own `restrictedFields`
 * already survives a later refetch (the reducer carries `prev?.restrictedFields`
 * forward through `pending`), so once we've seen one real result there's no
 * reason to fall back to the pessimistic "unconfirmed" default during a
 * routine background reload.
 *
 * Without this, `effectiveRestrictedFields` re-applied its pending fallback on
 * EVERY refetch's `loading: true` (not just the very first one), flipping
 * `canViewPlatform` false and back on every `loadAll()` cycle. `index.tsx`'s
 * effect depends on `canViewPlatform` and calls `loadAll()` when it changes —
 * which re-fetches `platformSignal` — which flips `loading` again: a
 * self-sustaining infinite request loop (discovered live-debugging flank#4655,
 * root cause is flank#4650's own design, not specific to that PR).
 */
const useHasEverResolved = (signal: PermissionSignal): boolean => {
  const resolvedRef = useRef(false);
  if (signal.reqId && !signal.loading && !signal.error) resolvedRef.current = true;
  return resolvedRef.current;
};

/**
 * flank#4745 review (CHANGES_REQUESTED): the prior check, `clientMoneyUnavailable`,
 * only asked whether a `client_spend`/`client_cpm` KEY is present on the row —
 * not its value. The API can legitimately send `client_spend: 0` (or `null`)
 * for an unpriced selection, which that check still read as "has data" and
 * would bring the option back for a white label with zero real Client Spend.
 *
 * `client_spend_priced_impressions` (ribeye-api#2481, shipped) is the real
 * value signal: it's on EVERY `totals` row, complete or incomplete, and is
 * actually 0 (not absent) when nothing in the selection resolved a sold CPM.
 * `totals` is ungrouped, so `rows[0]` is the one aggregate row to check.
 *
 * Missing/non-numeric is treated as "assume visible" (an older ribeye-api
 * without #2481, or some edge case we haven't seen) rather than silently
 * hiding the option on a signal that was never meant to gate it.
 */
export const deriveHasClientSpendData = (rows: ReportRow[], totalsEverResolved: boolean): boolean => {
  const priced = Number(rows[0]?.client_spend_priced_impressions);
  return !totalsEverResolved || !Number.isFinite(priced) || priced > 0;
};

/** Single source of truth for the Unified Reporting permission toggles
 * widgets kept re-implementing ad hoc, or not checking at all (flank#4623,
 * #4625, #4636) — see `derivePermissions` above for what each flag means. */
export const useReportingPermissions = (): ReportingPermissions => {
  const isRibeyeAdmin = useAppSelector(selectIsSuperAdmin);
  const totalsWidget = useAppSelector(selectReportingV2Widget('totals'));
  const platformSignalWidget = useAppSelector(selectReportingV2Widget('platformSignal'));
  const totalsEverResolved = useHasEverResolved(totalsWidget);
  const platformEverResolved = useHasEverResolved(platformSignalWidget);

  const restrictedFields = [
    ...effectiveRestrictedFields(
      totalsWidget,
      totalsEverResolved ? [] : ['metrics.spend', 'metrics.client_spend', 'metrics.conversions'],
    ),
    ...effectiveRestrictedFields(platformSignalWidget, platformEverResolved ? [] : ['dimensions.dsp']),
  ];

  // flank#4745 — `totals` always requests client_spend/client_cpm and is
  // always fetched on page load, so its rows are the same reliable
  // page-wide signal `restrictedFields` already is above: a widget that
  // hasn't itself requested client_spend yet (Top Campaigns, Top Publishers)
  // can still tell whether the CURRENT selection has any. Optimistic (true)
  // until totals actually resolves, so the option doesn't flash hidden
  // before the real check lands. See `deriveHasClientSpendData` for why this
  // is a value check, not a key-presence check.
  const hasClientSpendData = deriveHasClientSpendData(totalsWidget.rows, totalsEverResolved);

  return derivePermissions(isRibeyeAdmin, restrictedFields, hasClientSpendData);
};
